import { h, Component } from 'preact';
import cx from 'classnames';
import style from './style.less';
import { Text, Localizer } from 'preact-i18n';
import { Icon } from '@cube/blocks';
import { findStock } from '@os/cloudquote';

const UP_ARROW = 38;
const DOWN_ARROW = 40;
const DEFAULT_MAX_SEARCH_RESULTS = 6;
const CARRIAGE_RETURN = 13;

/**
 * An input box for finding symbols and adding them to a list of Comparing symbols.
 * @param {Object} props
 * @param {Function} [props.onSymbolSelect]      called when a symbol is selected by SymbolSuggestionPanel
 * @param {Boolean|String} [props.disabled]      if true, disabled the input. if @type {String}, replace the placeholder text with disabled
 * @param {String} [props.dropdownClass]         a class name for the suggestion box that drops down from the SymbolLookup
 * @param {Boolean|Object} [props.icon=true]     if true, display the default icon. if passed a React element, renders a custom icon (eg icon={<Icon name="ok" />})
 * @param {String} [props.placeholder]           the placeholder text for the <input> element.
 * @param {Number} [props.maxLength]             the maxlength attribute for the input box
 * @param {Number} [props.maxResults]            the max number of results to display in the Suggestions box
 * @param {String} [props.toUpperCase]           if true, the input box will force all input to upper case
 * @param {String} [props.class]                 class for the container element
 * @param {String|Object} [props.style]          style string or object for the container element
 */
export default class SymbolLookup extends Component {
	static defaultProps = {
		maxResults: DEFAULT_MAX_SEARCH_RESULTS
	}

	state = {
		value: '',
		showResults: false
	}

	handleBlur = (e) => {
		setTimeout(() => this.setState({ showResults: false }), 250);
	}

	handleFocus = (e) => {
		this.setState({ showResults: true });
	}

	handleOnChange = (e) => {
		let key = e.keyCode || e.charCode;
		let { value } = e.target;

		if (this.props.toUpperCase) { value = value.toUpperCase(); }
		this.setState({ value });

		if (key === CARRIAGE_RETURN) {
			this.state.value && this.suggestions.handleSymbolSelect();
		}
		else if (key === UP_ARROW) {
			this.suggestions.bumpCursor(-1);
			e.preventDefault(); // prevent the cursor moving in the input box
		}
		else if (key === DOWN_ARROW) {
			this.suggestions.bumpCursor(1);
			e.preventDefault(); // prevent the cursor moving in the input box
		}
	}

	handleSymbolSelect = (quote) => {
		if (quote) {
			let { trackingSource } = this.props,
				track = this.context && this.context.track;
			if (trackingSource && track && track.api && track.api.track) {
				let trackingType = (this.props.trackingType ? this.props.trackingType : 'trackgif'),
					trackingZone = (this.props.trackingZone ? this.props.trackingZone : 'finance-blocks');
				track.api.track({
					type: trackingType,
					id: trackingSource,
					symbol: quote.UniqueSymbol,
					zone: trackingZone
				});
			}

			this.props.onSymbolSelect && this.props.onSymbolSelect(quote);
			this.setState({ value: '' });
		}
	}

	handleSubmitButton = () => {
		this.suggestions.submit();
	}

	render({ disabled, dropdownClass, icon = true, maxLength, maxResults, placeholder = <Text id="placeholder" />, theme, ...props }, { value = '', showResults }) {
		const children = (
			<Localizer>
				<input
					type={'text'}
					maxlength={maxLength}
					value={value}
					placeholder={typeof disabled === 'string' ? disabled : placeholder}
					onFocus={this.handleFocus}
					onBlur={this.handleBlur}
					onkeyUp={this.handleOnChange}
					disabled={!!disabled}
				/>
			</Localizer>
		);

		icon = typeof icon === 'object'
			? icon
			: icon && <Icon name="search-cube" />;

		return (
			<span class={cx(props.class, style.symbolLookup, style[theme])}>
				<span class={cx(style.input, !icon && style.noIcon)} style={props.style}>
					{children}
					<SymbolSuggestionPanel
						class={dropdownClass}
						ref={(e) => this.suggestions = e/* a ref to call bumpCursor and get selections from child */}
						onSymbolSelect={this.handleSymbolSelect}
						visible={showResults && value.length > 0}
						maxResults={maxResults}
						searchTerm={value}
					/>
				</span>
				{icon && (
					<button onClick={this.handleSubmitButton} disabled={!!disabled} class={style.button}>
						{icon}
					</button>
				)}
			</span>
		);
	}
}

/**
 * An input box to search for a searchTerm and get a list of suggestion results from the CloudQuote API.
 * @param {Object} props
 * @param {Function} [props.onSymbolSelect]    called when a symbol is selected in the list
 * @param {Boolean} [props.visible]            if false, dont show the container
 * @param {String} [props.searchTerm]          the term to search for in the CloudQuote API
 * @param {String} [props.class]               class for the container element
 * @param {String|Object} [props.style]        style string or object for the container element
 */
export class SymbolSuggestionPanel extends Component {
	// Keep a simple ID of async calls because we only care about the most recent call.
	counter = 0;

	state = {
		cursor: 0,
		quotes: [],
		isSearching: false
	}

	handleSymbolSelect = (quote) => {
		const value = quote
			? quote
			: this.state.quotes.length && this.state.quotes[this.state.cursor];

		this.props.onSymbolSelect && this.props.onSymbolSelect(value);
		this.setState({ quotes: [] });
	}

	bumpCursor = (n = 1) => {
		let cursor = this.state.cursor + n;
		cursor = cursor < 0
			? this.state.quotes.length - 1
			: cursor >= this.state.quotes.length
				? 0
				: cursor;

		this.setState({ cursor });
	}

	submit() {
		const value = this.state.quotes.length && this.state.quotes[this.state.cursor];
		this.props.onSymbolSelect && this.props.onSymbolSelect(value);
		this.setState({ quotes: [] });
	}

	handleRowHover = (index) => {
		this.setState({ cursor: index });
	}

	doSearch = (searchTerm) => {
		let queryId = ++this.counter;
		let query = `SYMBOL/${searchTerm}|SYMBOL/${searchTerm}*|NAME/${searchTerm}*|WORD/${searchTerm}|WORD/${searchTerm}*`;
		!this.state.isSearching && this.setState({ isSearching: true });
		findStock(query)
			.then((data) => {
				// update state if the queryId for this call matches the current counter
				if (queryId === this.counter) {
					this.setState({ isSearching: false }, () => {
						// wait a tick until setState is done to guarentee re-render so this.props.searchTerm will be current
						data && this.setState({
							quotes: (data.rows || []).slice(0, this.props.maxResults),
							cursor: 0
						});
					});
				}
			});
	}

	componentWillReceiveProps(nextProps) {
		if (this.props.searchTerm !== nextProps.searchTerm) {
			this.doSearch(nextProps.searchTerm);
		}
	}

	render({ visible, maxResults, searchTerm, ...props }, { cursor, isSearching, quotes }) {
		props.class = cx(props.class, style['symbol-suggestions'], (!visible || (isSearching && !quotes.length)) && style.hidden);

		const renderedResults = quotes.length > 0 ? (
			quotes.map((quote, index) => (
				<SymbolSuggestionRow
					quote={quote}
					onSymbolSelect={this.handleSymbolSelect}
					onMouseEnter={this.handleRowHover}
					index={index}
					active={cursor === index}
				/>
			))
		) : !isSearching && ( <tr><td><Text id={'no-results'}>{'No results found, please try again.'}</Text></td></tr> );

		return (
			<div {...props}>
				<table>
					<tbody>
						{renderedResults}
					</tbody>
				</table>
			</div>
		);
	}
}

/**
 * A row with the Name and Symbol of a search result from the CloudQuote API
 * @param {Object} props
 * @param {Function} [props.onMouseEnter]       called when the mouse hovers over this row, called with props.index
 * @param {Function} [props.onSymbolSelect]     called when a row is clicked on, called with props.quote
 * @param {Object} [props.quote]                a stock quote from the CloudQuote API
 * @param {Boolean} [props.active]              true if this row is active, apply hilighting styles
 * @param {Number} [props.index]                the index of the row in a list of results
 * @param {String} [props.class]                class for the container element
 * @param {String|Object} [props.style]         style string or object for the container element
 */
export class SymbolSuggestionRow extends Component {
	handleMouseEnter = () => {
		this.props.onMouseEnter && this.props.onMouseEnter(this.props.index);
	}
	handleClick = () => {
		this.props.onSymbolSelect && this.props.onSymbolSelect(this.props.quote);
	}
	render({ quote = {}, active, ...props }) {
		return (
			<tr
				class={cx(props.class, style.result, active && style.active)}
				onClick={this.handleClick}
				onMouseEnter={this.handleMouseEnter}
				style={props.style}
			>
				<td class={style['result-name']}>{quote.Name}</td>
				<td class={style['result-symbol']}>{quote.Symbol}</td>
			</tr>
		);
	}
}



// WEBPACK FOOTER //
// ../node_modules/@cube/finance-blocks/src/components/symbol-lookup/index.js